import React from 'react';
import { SearchIcon, ShoppingBagIcon, MenuIcon, XIcon, ChevronDownIcon, ArrowRightIcon } from 'lucide-react';
import { shopMenu, helpMenu, aboutMenu } from '../data/siteContent';
import { PageHero } from '../components/PageHero';
import { SectionHeading } from '../components/SectionHeading';
type Item = {
  label: string;
  to: string;
};
const primary = [{
  label: 'Home',
  active: true
}, {
  label: 'About',
  dropdown: true
}, {
  label: 'Shop',
  dropdown: true
}, {
  label: 'Gifting'
}, {
  label: 'Size Guide'
}, {
  label: 'Stockists'
}, {
  label: 'Help',
  dropdown: true
}];

/**
 * /navigation — every navigation state rendered flat and static, so the
 * whole menu system can be exported to Figma in one pass. Labels come from
 * the same data the live nav uses, so this sheet cannot drift.
 */
export function NavigationKit() {
  return <div className="w-full bg-paper" data-id="element-559" data-mp="2wwqadyzq1o9k">
      <PageHero eyebrow="Navigation kit" title="Every menu state, in one place." intro="The header, all three dropdowns and the mobile menu, rendered open and static for export. Nothing here is interactive — it is a specimen sheet." data-id="element-560" data-mp="2swpq3xpxyiul" />

      <div className="mx-auto max-w-[1400px] px-6 py-20 md:py-24" data-id="element-561" data-mp="3jiiu64q9zdj0">
        {/* ── DESKTOP HEADER ─────────────────────────────────── */}
        <SectionHeading eyebrow="01 — Desktop header" title="Default state." subtitle="Sticky, 73px tall, Paper at 95% with a blur behind it and an Oat Milk hairline below." data-id="element-562" data-mp="39lxnzg2irwdl" />
        <Frame label="Header · default" data-id="element-563" data-mp="64xo32irus0x">
          <HeaderBar data-id="element-564" data-mp="67tjok75d4m7" />
        </Frame>

        {/* ── DROPDOWNS ──────────────────────────────────────── */}
        <div className="mt-24" data-id="element-565" data-mp="3sljyq0zhswkk">
          <SectionHeading eyebrow="02 — Dropdowns" title="One pattern, three menus." subtitle="A full-width Paper Warm panel below the header. Links are DM Serif Display at 24px with an arrow that appears on hover." data-id="element-566" data-mp="3sks16kmc4orn" />

          <Frame label="About · open" data-id="element-567" data-mp="ysgt9f8oss2a">
            <HeaderBar open="About" data-id="element-568" data-mp="yrfh17fxpziz" />
            <DropdownPanel items={aboutMenu} columns={1} data-id="element-569" data-mp="yqcrp3y9g556" />
          </Frame>

          <Frame label="Shop · open" data-id="element-570" data-mp="us6lbx1c3rks">
            <HeaderBar open="Shop" data-id="element-571" data-mp="up0vgfkvslo0" />
            <DropdownPanel items={shopMenu} columns={2} data-id="element-572" data-mp="pqzga5qkt6ht" />
          </Frame>

          <Frame label="Help · open" data-id="element-573" data-mp="ksxwtjp8n55t">
            <HeaderBar open="Help" data-id="element-574" data-mp="krwbmmeid2ad" />
            <DropdownPanel items={helpMenu} columns={1} data-id="element-575" data-mp="kv1z5x7hnw3z" />
          </Frame>
        </div>

        {/* ── MOBILE ─────────────────────────────────────────── */}
        <div className="mt-24" data-id="element-576" data-mp="b22znkagel5l">
          <SectionHeading eyebrow="03 — Mobile" title="Full-screen menu." subtitle="Below 1024px the nav collapses to a hamburger. The panel takes the whole viewport, with the three dropdowns as accordions." data-id="element-577" data-mp="b0rw9lkx7ff0" />

          <div className="mt-12 grid gap-8 md:grid-cols-2 lg:grid-cols-3" data-id="element-578" data-mp="3t7g0zac260k5">
            <MobileFrame label="Closed" data-id="element-579" data-mp="3o8wcb262yao3">
              <MobileHeaderBar data-id="element-580" data-mp="3o9xyo2sjqjl4" />
            </MobileFrame>

            <MobileFrame label="Menu open" data-id="element-581" data-mp="3e5zfu89jzkqa">
              <MobileMenu data-id="element-582" data-mp="3e57tzp5rd1d7" />
            </MobileFrame>

            <MobileFrame label="Shop expanded" data-id="element-583" data-mp="3j4173gh4uqcu">
              <MobileMenu expanded data-id="element-584" data-mp="3j2pv4tdjacan" />
            </MobileFrame>
          </div>
        </div>

        {/* ── SPECS ──────────────────────────────────────────── */}
        <div className="mt-24" data-id="element-585" data-mp="3t3094g7e7nqv">
          <SectionHeading eyebrow="04 — Specs" title="Measurements." data-id="element-586" data-mp="3t1ywi7aqat71" />
          <div className="mt-12 grid gap-x-16 gap-y-10 md:grid-cols-2 lg:grid-cols-3" data-id="element-587" data-mp="2vg18judkxvnp">
            {[{
            title: 'Header',
            rows: [['Height', '73px'], ['Background', 'Paper @ 95% + blur'], ['Border', '1px Oat Milk @ 40%'], ['Padding', '24px horizontal · 20px vertical']]
          }, {
            title: 'Nav item',
            rows: [['Style', 'CTA/Primary · 11px caps'], ['Tracking', '+9% (+90)'], ['Padding', '14px × 10px'], ['Default', 'Black Coffee @ 60%'], ['Hover / active', 'Black Coffee 100%']]
          }, {
            title: 'Dropdown panel',
            rows: [['Background', 'Paper Warm'], ['Padding', '40px vertical'], ['Link style', 'Display/H3 · 24px'], ['Row gap', '12px · column gap 64px'], ['Columns', 'Shop 2 · About & Help 1']]
          }, {
            title: 'Announcement bar',
            rows: [['Background', 'Black Coffee'], ['Text', 'Eyebrow/Small · Paper @ 90%'], ['Height', '38px']]
          }, {
            title: 'Mobile menu',
            rows: [['Trigger', 'Hamburger, below 1024px'], ['Panel', 'Full viewport, Paper'], ['Link style', 'Display/H2 · 30px'], ['Row divider', '1px Oat Milk @ 40%'], ['Row height', '68px']]
          }, {
            title: 'Icons',
            rows: [['Set', 'Lucide'], ['Size', '18px header · 20px hamburger'], ['Stroke', '1.5'], ['Hit area', '40px circle']]
          }].map(g => <div key={g.title} className="border-t border-oat pt-7" data-id="element-588" data-mp="1ecs8vuiecib9">
                <h3 className="type-display text-2xl text-coffee" data-id="element-589" data-mp="zivzdsai9jmg">{g.title}</h3>
                <dl className="mt-5 space-y-2.5" data-id="element-590" data-mp="14fvz7lgrhq54">
                  {g.rows.map(([k, v]) => <div key={k} className="flex items-baseline justify-between gap-6" data-id="element-591" data-mp="2tvdjir01s3mv">
                      <dt className="type-body text-sm text-ink/55" data-id="element-592" data-mp="2yscbp405i0if">{k}</dt>
                      <dd className="type-ui text-right text-sm text-coffee" data-id="element-593" data-mp="2xf44vflfrb4v">
                        {v}
                      </dd>
                    </div>)}
                </dl>
              </div>)}
          </div>
        </div>
      </div>
    </div>;
}

/* ── Specimen frames ─────────────────────────────────────── */

function Frame({
  label,
  children
}: {
  label: string;
  children: React.ReactNode;
}) {
  return <figure className="mt-12" data-id="element-594" data-mp="3b4fssnzc2t0c">
      <figcaption className="type-eyebrow mb-4 text-[9px] text-coffee/45" data-id="element-595" data-mp="3b68yo124h97a">
        {label}
      </figcaption>
      <div className="overflow-hidden rounded-3xl border border-oat" data-id="element-596" data-mp="36co7osyy5fhl">
        {children}
      </div>
    </figure>;
}
function MobileFrame({
  label,
  children
}: {
  label: string;
  children: React.ReactNode;
}) {
  return <figure data-id="element-597" data-mp="2qyywzlpgg2o">
      <figcaption className="type-eyebrow mb-4 text-[9px] text-coffee/45" data-id="element-598" data-mp="3u423xa07xv7u">
        {label}
      </figcaption>
      <div className="h-[560px] overflow-hidden rounded-3xl border border-oat bg-paper" data-id="element-599" data-mp="1glp8zcis15x2">
        {children}
      </div>
    </figure>;
}

/* ── Static nav specimens ────────────────────────────────── */

function HeaderBar({
  open
}: {
  open?: string;
}) {
  return <div className="bg-paper" data-id="element-600" data-mp="1po6xjxlwman8">
      <div className="bg-coffee" data-id="element-601" data-mp="1ulgl17pqgc8f">
        <div className="flex items-center justify-center px-6 py-2.5" data-id="element-602" data-mp="1umi7s9g42n5z">
          <p className="type-eyebrow text-[10px] text-paper/90" data-id="element-603" data-mp="1zkti0labizoq">
            Free nationwide delivery over R 750
          </p>
        </div>
      </div>
      <div className="flex items-center justify-between gap-8 border-b border-oat/40 px-6 py-5" data-id="element-604" data-mp="24h0bhelj2ips">
        <span className="type-display shrink-0 text-[26px] text-coffee" data-id="element-605" data-mp="29bxuneciu8ge">
          Ecoffee Cup<sup className="ml-0.5 align-super text-[10px]" data-id="element-606" data-mp="2dan631e01fxe">®</sup>
        </span>
        <nav className="hidden items-center gap-0.5 lg:flex" data-id="element-607" data-mp="2ibue91k20h4l">
          {primary.map(n => {
          const isOpen = open === n.label;
          const tone = isOpen || n.active ? 'text-coffee' : 'text-coffee/60';
          return <span key={n.label} className={`type-cta flex items-center gap-1.5 rounded-full px-3.5 py-2.5 text-[11px] ${tone}`} data-id="element-608" data-mp="1t7ci8249dtos">
                {n.label}
                {n.dropdown && <ChevronDownIcon className={`h-3 w-3 ${isOpen ? 'rotate-180' : ''}`} strokeWidth={2} aria-hidden="true" data-id="element-609" data-mp="236l92m3tus6x" />}
              </span>;
        })}
        </nav>
        <div className="flex items-center gap-1 text-coffee" data-id="element-610" data-mp="1o8t06cssd21p">
          <span className="p-2.5" data-id="element-611" data-mp="1oa4184uujvp4">
            <SearchIcon className="h-[18px] w-[18px]" strokeWidth={1.5} data-id="element-612" data-mp="1jh14jnurw64p" />
          </span>
          <span className="p-2.5" data-id="element-613" data-mp="vrq98lw6z3xy">
            <ShoppingBagIcon className="h-[18px] w-[18px]" strokeWidth={1.5} data-id="element-614" data-mp="vqnlgmu61hm9" />
          </span>
        </div>
      </div>
    </div>;
}
function DropdownPanel({
  items,
  columns
}: {
  items: Item[];
  columns: 1 | 2;
}) {
  return <div className="border-b border-oat/40 bg-paper-warm" data-id="element-615" data-mp="hkln9slduu0v">
      <div className="px-6 py-10" data-id="element-616" data-mp="mdybi1m5gyt3">
        <ul className={`grid gap-x-16 gap-y-3 ${columns === 2 ? 'sm:grid-cols-2 lg:max-w-3xl' : 'lg:max-w-md'}`} data-id="element-617" data-mp="mf9bdqch5mex">
          {items.map(i => <li key={i.label} data-id="element-618" data-mp="cnmy7ogktd9j">
              <span className="type-display flex items-center gap-2.5 text-2xl text-coffee" data-id="element-619" data-mp="10d7fm2x302gp">
                {i.label}
                <ArrowRightIcon className="h-4 w-4 -translate-x-1 opacity-0" strokeWidth={1.5} aria-hidden="true" data-id="element-620" data-mp="15cal1ogz4ap7" />
              </span>
            </li>)}
        </ul>
      </div>
    </div>;
}
function MobileHeaderBar() {
  return <div data-id="element-621" data-mp="3b3xrndzpamub">
      <div className="bg-coffee px-5 py-2.5 text-center" data-id="element-622" data-mp="3b47hn1kqwu00">
        <p className="type-eyebrow text-[9px] text-paper/90" data-id="element-623" data-mp="3g3tvnd8eee9v">
          Free delivery over R 750
        </p>
      </div>
      <div className="flex items-center justify-between border-b border-oat/40 px-5 py-5 text-coffee" data-id="element-624" data-mp="16dfioxvw0j4m">
        <span className="type-display text-2xl" data-id="element-625" data-mp="1bafqbd8dxtxu">
          Ecoffee Cup<sup className="align-super text-[9px]" data-id="element-626" data-mp="wmkv9q6wndhl">®</sup>
        </span>
        <div className="flex items-center gap-1" data-id="element-627" data-mp="wmb6u71idee0">
          <SearchIcon className="h-[18px] w-[18px]" strokeWidth={1.5} data-id="element-628" data-mp="11kw5w350xs0v" />
          <span className="w-3" data-id="element-629" data-mp="nlwieyt8zj6a" />
          <MenuIcon className="h-5 w-5" strokeWidth={1.5} data-id="element-630" data-mp="nlmy9ruvw68i" />
        </div>
      </div>
    </div>;
}
function MobileMenu({
  expanded = false
}: {
  expanded?: boolean;
}) {
  return <div className="h-full overflow-hidden bg-paper" data-id="element-631" data-mp="dnywihyv1ygu">
      <div className="flex items-center justify-between px-5 py-5" data-id="element-632" data-mp="dpjmrve56v8w">
        <span className="type-display text-2xl text-coffee" data-id="element-633" data-mp="inuwm64ft5r4">
          Ecoffee Cup<sup className="align-super text-[9px]" data-id="element-634" data-mp="281zu6ziocffc">®</sup>
        </span>
        <XIcon className="h-5 w-5 text-coffee" strokeWidth={1.5} data-id="element-635" data-mp="27zx3ed9p6i9k" />
      </div>
      <nav className="px-5" data-id="element-636" data-mp="2d01m89y2vp2y">
        <MobileRow label="Home" data-id="element-637" data-mp="1dh9thlwlu9c" />
        <MobileRow label="About" chevron data-id="element-638" data-mp="1cps9ap55x22" />
        <MobileRow label="Shop" chevron open={expanded} data-id="element-639" data-mp="1bemj5c4yf8b" />
        {expanded && <ul className="border-b border-oat/40 pb-5" data-id="element-640" data-mp="3slddlqndtk9m">
            {shopMenu.map(i => <li key={i.label} className="type-body py-2 text-base text-ink/65" data-id="element-641" data-mp="b42f1nw31x18">
                {i.label}
              </li>)}
          </ul>}
        <MobileRow label="Gifting" data-id="element-642" data-mp="k25rlup3bjgw" />
        <MobileRow label="Size & Drink Guide" data-id="element-643" data-mp="g2dovymdo7jg" />
        {!expanded && <>
            <MobileRow label="Stockists" data-id="element-644" data-mp="g4qaclrch019" />
            <MobileRow label="Help" chevron data-id="element-645" data-mp="u13obkwo6jaz" />
          </>}
      </nav>
    </div>;
}
function MobileRow({
  label,
  chevron = false,
  open = false
}: {
  label: string;
  chevron?: boolean;
  open?: boolean;
}) {
  return <div className={`flex items-center justify-between py-5 ${open ? '' : 'border-b border-oat/40'}`} data-id="element-646" data-mp="2vtrv248ivovi">
      <span className="type-display text-3xl text-coffee" data-id="element-647" data-mp="uraajm3y75ag">{label}</span>
      {chevron && <ChevronDownIcon className={`h-4 w-4 text-coffee/50 ${open ? 'rotate-180' : ''}`} strokeWidth={1.5} aria-hidden="true" data-id="element-648" data-mp="zqdef386z5gu" />}
    </div>;
}